import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { initSession, isSessionValid } from '../lib/session';

const MAX_LOGIN_ATTEMPTS = 10;
const BLOCK_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

interface LoginAttempt {
  attempts: number;
  lastAttempt: number;
  blockedUntil?: number;
}

const AdminLogin = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(MAX_LOGIN_ATTEMPTS);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in with a valid session
    if (isSessionValid()) {
      navigate('/admin/dashboard');
      return;
    }
    checkLoginStatus();
  }, [navigate]);

  useEffect(() => {
    let timer: number | undefined;
    if (blockTimeRemaining !== null && blockTimeRemaining > 0) {
      timer = window.setInterval(() => {
        setBlockTimeRemaining(prev => prev !== null ? prev - 1000 : null);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [blockTimeRemaining]);

  const checkLoginStatus = () => {
    const attempts = getLoginAttempts();
    if (attempts.blockedUntil && attempts.blockedUntil > Date.now()) {
      setBlockTimeRemaining(attempts.blockedUntil - Date.now());
      setRemainingAttempts(0);
    } else if (attempts.blockedUntil) {
      // Reset if block has expired
      clearLoginAttempts();
      setRemainingAttempts(MAX_LOGIN_ATTEMPTS);
    } else {
      setRemainingAttempts(MAX_LOGIN_ATTEMPTS - (attempts.attempts || 0));
    }
  };

  const getLoginAttempts = (): LoginAttempt => {
    const stored = localStorage.getItem('loginAttempts');
    return stored ? JSON.parse(stored) : { attempts: 0, lastAttempt: Date.now() };
  };

  const updateLoginAttempts = (success: boolean) => {
    const attempts = getLoginAttempts();
    
    if (success) {
      clearLoginAttempts();
      return;
    }

    const now = Date.now();
    const newAttempts = {
      attempts: (attempts.attempts || 0) + 1,
      lastAttempt: now
    };

    if (newAttempts.attempts >= MAX_LOGIN_ATTEMPTS) {
      newAttempts.blockedUntil = now + BLOCK_DURATION;
      setBlockTimeRemaining(BLOCK_DURATION);
    }

    localStorage.setItem('loginAttempts', JSON.stringify(newAttempts));
    setRemainingAttempts(MAX_LOGIN_ATTEMPTS - newAttempts.attempts);
  };

  const clearLoginAttempts = () => {
    localStorage.removeItem('loginAttempts');
    setRemainingAttempts(MAX_LOGIN_ATTEMPTS);
    setBlockTimeRemaining(null);
  };

  const formatTimeRemaining = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if currently blocked
    const attempts = getLoginAttempts();
    if (attempts.blockedUntil && attempts.blockedUntil > Date.now()) {
      setError(`Zu viele Anmeldeversuche. Bitte warten Sie ${formatTimeRemaining(attempts.blockedUntil - Date.now())}.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (password === 'StAr2022!') {
        initSession(); // Initialize new session
        updateLoginAttempts(true);
        navigate('/admin/dashboard');
      } else {
        updateLoginAttempts(false);
        if (remainingAttempts <= 1) {
          setError(`Zu viele Anmeldeversuche. Bitte warten Sie ${formatTimeRemaining(BLOCK_DURATION)}.`);
        } else {
          setError(`Ungültiges Passwort. Noch ${remainingAttempts - 1} Versuch${remainingAttempts - 1 !== 1 ? 'e' : ''} übrig.`);
        }
      }
    } catch (err) {
      setError('Ein Fehler ist aufgetreten');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-green-50 flex items-center justify-center p-4 admin-dashboard">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h1 className="text-2xl font-bold text-green-800 mb-6 text-center">Admin Login</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {blockTimeRemaining !== null && blockTimeRemaining > 0 ? (
          <div className="text-center">
            <p className="text-red-600 mb-2">Zu viele Anmeldeversuche</p>
            <p className="text-gray-600">
              Bitte warten Sie noch {formatTimeRemaining(blockTimeRemaining)}
            </p>
          </div>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Passwort
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
                disabled={loading || blockTimeRemaining !== null}
              />
            </div>

            {remainingAttempts < MAX_LOGIN_ATTEMPTS && remainingAttempts > 0 && (
              <p className="text-sm text-gray-600">
                Noch {remainingAttempts} Versuch{remainingAttempts !== 1 ? 'e' : ''} übrig
              </p>
            )}

            <button
              type="submit"
              disabled={loading || blockTimeRemaining !== null}
              className="w-full bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Anmeldung...' : 'Anmelden'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;